import { useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../../Rajasthan/Utility/const";
import loader from "../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../extensions/date";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../../../utility/displayText";
interface SummaryData {
  date: string;
  month: string;
  oar: number;
  gna_cost: number;
  gna_quantum: number;
  tgna_cost: number;
  tgna_quantum: number;
  optimal_gna: number;
  total_cost: number;
  slot: number;
}

export default function MonthCostAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
  selectedTargetDate,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  data: SummaryData[];
  selectedTargetDate: any[];
}) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const chartSuffixes = [
    {
      suffix: "tgna_cost",
      displayName: "Simulated TGNA Cost",
      stackSuffix: "tgna_cost",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Bar,
      unit: "Lakh(₹)",
      yAxisId: "right",
    },
    {
      suffix: "gna_cost",
      displayName: "Simulated GNA Cost",
      stackSuffix: "gna_cost",
      stroke: "rgba(241, 147, 92, 1)",
      chartType: ChartType.Bar,
      unit: "Lakh(₹)",
      yAxisId: "right",
    },
    {
      suffix: "optimal_gna",
      displayName: "Simulated GNA",
      stackSuffix: "optimal_gna",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      isDash:true
    },
    {
      suffix: "oar",
      displayName: "Total Open Access Requirement",
      stackSuffix: "oar",
      stroke: "rgba(17, 141, 255, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "total_cost",
      displayName: "Simulated Total Cost",
      stackSuffix: "total_cost",
      stroke: "rgba(255, 194, 158, 1)",
      chartType: ChartType.Line,
      unit: "Lakh(₹)",
      yAxisId: "right",
    },
  ];
  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
    chartSuffixes[4],
  ]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  useEffect(() => {
    let filteredData = data;
     if (selectedTargetDate.length > 0) {
      const selectedMonthYearCodes = Array.from(
        new Set(
          selectedTargetDate.map((t: any) => {
            const dateStr =
              t.value.includes("-") && t.value.split("-")[0].length === 2
                ? t.value.split("-").reverse().join("-")
                : t.value;
            return `${getMonthFromDate(dateStr)}-${dateStr.split("-")[0]}`;
          })
        )
      );
      filteredData = filteredData.filter((row) =>
        selectedMonthYearCodes.includes(
          `${getMonthFromDate(row.date)}-${row.date.split("-")[0]}`
        )
      );
    }

    const mappedData = filteredData.map((item) => ({
      ...item,
      monthYear: `${getMonthFromDate(item.date)}-${item.date.split("-")[0]}`,
    }));


    const par: ChartDataParams = {
      keys: ["monthYear"],
      tableKeys: ["monthYear"],
      groupBy: [""],
      volumeKey: ``,
      aggregationKeys: [
        "gna_cost",
        "tgna_cost",
        "optimal_gna",
        "oar",
        "total_cost",
      ],
      averageKeys: [],
      averageDuplicationKeys: ["monthYear"],
      priceKey: "",
      data: mappedData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedBlock, selectedTargetDate]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string,
    isDash:boolean
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: legendColor,
      type: ["optimal_gna", "oar"].includes(suffix)
        ? ChartType.Line
        : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      yAxisId: yAxisId,
      stackId: undefined,
      isDash:isDash
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
        isDash
      }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId,
              isDash
            ) as any
        )
    );
  }

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={chartData.length > 96 * 2 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={false}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel="MW"
          secondYAxisLabel="Lakh(₹)"
          nameGap={40}
          xDataKey={"monthYear"}
          top={10} // Top margin for grid
          bottom={30} // Bottom margin for grid
          xTick={(value: any, index: any) => {
            return renderHourTick(value, 10);
          }}
          // secondXDataKey={"month"}
          showToolbox={false}
        />
      )}
    </>
  );
}
